/**
 * GPS consent state machine (P4).
 *
 * Maps the four `GpsMode` values to a concrete consent decision without ever
 * surprising the user:
 *   - `off`      → never query, never prompt. Returns "skip".
 *   - `silent`   → only read if permission is ALREADY granted; never prompt.
 *   - `prompt`   → query, allowing the browser permission prompt (SEON-parity default).
 *   - `required` → same as prompt, but the host page treats denial as fatal.
 *
 * The decision is derived from the Permissions API where available, falling
 * back to "prompt" semantics on browsers (Safari) that don't expose
 * geolocation permission state.
 */

import type { GpsMode } from "../types.ts";

export type ConsentDecision = "skip" | "read-if-granted" | "may-prompt";

export type PermissionStateValue = "granted" | "denied" | "prompt" | "unavailable";

/** Query the current geolocation permission without triggering a prompt. */
export async function queryGeoPermission(): Promise<PermissionStateValue> {
  try {
    const perms = (navigator as unknown as {
      permissions?: { query?: (d: { name: string }) => Promise<{ state: string }> };
    }).permissions;
    if (!perms?.query) return "unavailable";
    const status = await perms.query({ name: "geolocation" });
    const s = status.state;
    if (s === "granted" || s === "denied" || s === "prompt") return s;
    return "unavailable";
  } catch {
    return "unavailable";
  }
}

/** Resolve the consent decision for a given mode + current permission state. */
export function decideConsent(mode: GpsMode, perm: PermissionStateValue): ConsentDecision {
  switch (mode) {
    case "off":
      return "skip";
    case "silent":
      // Only proceed if the user has already explicitly granted access.
      return perm === "granted" ? "read-if-granted" : "skip";
    case "prompt":
    case "required":
      // Denied means a prompt won't appear anyway — don't waste the budget.
      if (perm === "denied") return "skip";
      return "may-prompt";
    default:
      return "skip";
  }
}
